import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api, { eur } from "@/lib/api";

export const SearchOverlay = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
    else {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { search: query, limit: 6 } });
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const go = (slug) => {
    onClose();
    navigate(`/produit/${slug}`);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    onClose();
    navigate(`/boutique?search=${encodeURIComponent(query)}`);
  };

  if (!open) return null;

  return (
    <div data-testid="search-overlay" className="fixed inset-0 z-[60] animate-fade-in">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-cream w-full max-h-[88vh] overflow-y-auto">
        <div className="max-w-3xl mx-auto px-5 md:px-8 pt-7 pb-10">
          <div className="flex items-center justify-between mb-6">
            <p className="text-xs uppercase tracking-[0.2em] text-ash">Recherche</p>
            <button data-testid="close-search-btn" onClick={onClose} aria-label="Fermer">
              <X className="h-6 w-6 text-ink" strokeWidth={1.5} />
            </button>
          </div>
          <form onSubmit={submit} className="flex items-center gap-3 border-b-2 border-ink pb-3">
            <Search className="h-6 w-6 text-ink" strokeWidth={1.5} />
            <input
              ref={inputRef}
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un luminaire, un style..."
              className="flex-1 bg-transparent outline-none text-xl md:text-2xl font-serif placeholder:text-ash text-ink"
            />
          </form>

          <div className="mt-7">
            {loading && <p className="text-sm text-ash font-mono">Recherche...</p>}
            {!loading && query && results.length === 0 && (
              <p className="text-sm text-ash">Aucun résultat pour « {query} ».</p>
            )}
            <div className="grid gap-2">
              {results.map((p) => (
                <button
                  key={p.id}
                  data-testid={`search-result-${p.slug}`}
                  onClick={() => go(p.slug)}
                  className="flex items-center gap-4 p-2 hover:bg-sand transition-colors text-left rounded-sm"
                >
                  <img src={p.image} alt={p.name} className="h-16 w-16 object-cover bg-sand rounded-sm" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-ash">{p.category}</p>
                    <p className="font-sans font-medium text-ink">{p.name}</p>
                  </div>
                  <span className="font-mono text-sm text-ink">{eur(p.price)}</span>
                </button>
              ))}
            </div>
            {!query && (
              <div className="flex flex-wrap gap-2">
                {["Suspension", "LED", "Vintage", "Salon", "Plafonnier"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setQuery(s)}
                    className="text-xs uppercase tracking-[0.15em] border border-stone px-4 py-2 hover:border-ink transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
